import { Expert } from "@/app/api/__generated__/models/expert";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/atoms/Avatar/Avatar";
import { Button } from "@/components/atoms/Button/Button";
import { Icon } from "@/components/atoms/Icon/Icon";
import { Text } from "@/components/atoms/Text/Text";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/molecules/DropdownMenu/DropdownMenu";
import { ArrowDown01Icon } from "@hugeicons/core-free-icons";

export const MENU_LABEL = "Install for an expert";

interface Props {
  experts: Expert[];
  isAdding: boolean;
  onAddToLibrary: () => void;
  onAddToExpert: (expert: Expert) => void;
}

/** Installing to your own skills is the main action; the dropdown hands the
 *  skill to one of your experts instead. */
export function InstallSkillButton({
  experts,
  isAdding,
  onAddToLibrary,
  onAddToExpert,
}: Props) {
  if (experts.length === 0) {
    return (
      <Button
        variant="primary"
        size="small"
        onClick={onAddToLibrary}
        loading={isAdding}
        className="w-full sm:w-auto"
        data-testid="skill-install-button"
      >
        Install skill
      </Button>
    );
  }

  return (
    <div className="inline-flex w-full sm:w-auto" data-sentry-component="InstallSkillButton" data-sentry-source-file="InstallSkillButton.tsx">
      <Button
        variant="primary"
        size="small"
        onClick={onAddToLibrary}
        loading={isAdding}
        // oxlint-disable-next-line design-system/no-restyle -- the left half of a split button; Button has no split variant
        className="!rounded-r-none border-r-0 sm:w-auto"
        data-testid="skill-install-button"
      data-sentry-element="Button" data-sentry-source-file="InstallSkillButton.tsx">
        Install skill
      </Button>
      <ExpertInstallMenu
        experts={experts}
        isAdding={isAdding}
        onAddToExpert={onAddToExpert}
      data-sentry-element="ExpertInstallMenu" data-sentry-source-file="InstallSkillButton.tsx">
        <Button
          variant="primary"
          size="small"
          aria-label={MENU_LABEL}
          disabled={isAdding}
          // oxlint-disable-next-line design-system/no-restyle -- the right half of a split button; Button has no split variant
          className="!min-w-0 !rounded-l-none border-l border-l-action-foreground/25"
        data-sentry-element="Button" data-sentry-source-file="InstallSkillButton.tsx">
          <Icon icon={ArrowDown01Icon} size={16} aria-hidden data-sentry-element="Icon" data-sentry-source-file="InstallSkillButton.tsx" />
        </Button>
      </ExpertInstallMenu>
    </div>
  );
}

interface MenuProps {
  experts: Expert[];
  isAdding: boolean;
  onAddToExpert: (expert: Expert) => void;
  children: React.ReactNode;
}

export function ExpertInstallMenu({
  experts,
  isAdding,
  onAddToExpert,
  children,
}: MenuProps) {
  return (
    <DropdownMenu data-sentry-element="DropdownMenu" data-sentry-component="ExpertInstallMenu" data-sentry-source-file="InstallSkillButton.tsx">
      <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="InstallSkillButton.tsx">{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[15rem]" data-sentry-element="DropdownMenuContent" data-sentry-source-file="InstallSkillButton.tsx">
        <DropdownMenuLabel data-sentry-element="DropdownMenuLabel" data-sentry-source-file="InstallSkillButton.tsx">
          <Text variant="small-tight" as="div" tone="muted" data-sentry-element="Text" data-sentry-source-file="InstallSkillButton.tsx">
            {MENU_LABEL}
          </Text>
        </DropdownMenuLabel>
        {experts.map((expert) => (
          <DropdownMenuItem
            key={expert.id}
            disabled={isAdding}
            onSelect={() => onAddToExpert(expert)}
            // oxlint-disable-next-line design-system/no-restyle -- a taller row for the expert's avatar and role
            className="gap-2.5 py-2"
          >
            <Avatar className="h-6 w-6">
              {expert.avatar_url ? (
                <AvatarImage src={expert.avatar_url} alt="" />
              ) : null}
              <AvatarFallback>{expert.name}</AvatarFallback>
            </Avatar>
            <span className="min-w-0">
              <span className="block truncate text-foreground-strong">
                {expert.name}
              </span>
              {expert.role ? (
                <span className="block truncate text-caption text-foreground-tertiary">
                  {expert.role}
                </span>
              ) : null}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
